import React, { useState, useEffect, useCallback } from "react";
import "./PlatformContent.scss";
import PlatformCard from "./PlatformCard";
import {
  PulseButton,
  PulseButtonTheme,
  PulseDropdown,
  PulseSpinnerIcon,
  PulsePopupContainerSize,
} from "@pulselabs-ai/shared-fe-ui";
import {
  AddRoundedIcon,
  TrashcanIcon,
} from "@pl-flight-recorder/web/components/icons";
import { useAppSelector } from "@pl-flight-recorder/web/store/hooks";
import { useParams } from "react-router-dom";
import MfaSDKCodeService, {
  SDKCodeResponse,
} from "@pl-flight-recorder/web/services/MfaSDKCodeService";
import { ProjectSDKCodeStatus } from "@pl-flight-recorder/common/models/enum/project-sdk-code-status";
import InstallSDKModal from "@pl-flight-recorder/web/components/InstallSDKModal/InstallSDKModal";
import CustomDialog from "@pl-flight-recorder/web/components/customDialog/CustomDialog";
import { PulsePopupConfirmation } from "@pl-flight-recorder/web/components/PulsePopupConfirmation/PulsePopupConfirmation";
import SDKViewer from "@pl-flight-recorder/web/components/SDKViewer/SDKViewer";
import { formatSDKInstructionsV2 } from "@pl-flight-recorder/web/utils/sdkInstructionsFormatter";

// interface PlatformContentProps {
//   // projectId: string;
// }

const PlatformContent: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { user } = useAppSelector((state) => state.user);
  const token = user?.token;

  // Status filter state
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [sdkProjects, setSdkProjects] = useState<SDKCodeResponse[]>([]);
  const [isInstallModalOpen, setIsInstallModalOpen] = useState(false);
  // State for delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // State for the View Code modal
  const [isViewCodeModalOpen, setIsViewCodeModalOpen] = useState(false);
  const [selectedSdkProject, setSelectedSdkProject] =
    useState<SDKCodeResponse | null>(null);

  // Wrap fetchSDKProjects in useCallback to prevent unnecessary recreation
  const fetchSDKProjects = useCallback(async () => {
    if (!projectId || !token) {
      console.log("Project ID or Token not available");
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const mfaSDKCodeService = MfaSDKCodeService.getInstance();
      const statusParam = statusFilter !== "all" ? statusFilter : undefined;
      const response = await mfaSDKCodeService.getSDKListByApp(
        token,
        projectId,
        statusParam,
      );
      console.log("Fetched SDK Project List:", response);
      setSdkProjects(response);
    } catch (err) {
      console.error("Failed to fetch SDK project list:", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch platform data",
      );
    } finally {
      setIsLoading(false);
    }
  }, [projectId, token, statusFilter]);

  useEffect(() => {
    fetchSDKProjects(); // Call the memoized function
  }, [fetchSDKProjects]); // Depend on the memoized function itself

  // Function to add a new platform
  const handleAddPlatform = () => {
    setIsInstallModalOpen(true);
  };

  // Updated close handler
  const handleCloseInstallModal = () => {
    setIsInstallModalOpen(false);
    // Refresh the list after closing the modal
    fetchSDKProjects();
  };

  // Handler to be passed to PlatformCard's onDelete prop
  const handleShowDeleteConfirm = (sdkProject: SDKCodeResponse) => {
    // Log the parameters when opening the confirm dialog
    console.log("Parameters for potential delete:", sdkProject.parameters);
    // setPlatformToDelete(sdkProject);
    setShowDeleteConfirm(true);
  };

  // Handler to actually perform deletion (API call placeholder)
  // const handleConfirmDelete = () => {
  //   console.log("Attempting to delete:", platformToDelete);
  //   // --- Placeholder for future API call ---
  //   // try {
  //   //   await mfaSDKCodeService.deleteSDKProject(platformToDelete.id, token);
  //   //   fetchSDKProjects(); // Refresh list on success
  //   // } catch (err) {
  //   //   setError("Failed to delete platform");
  //   // } finally {
  //   //    setShowDeleteConfirm(false);
  //   //    setPlatformToDelete(null);
  //   // }
  //   // --- End Placeholder ---
  //   setShowDeleteConfirm(false); // Close modal for now
  //   setPlatformToDelete(null);
  // };

  const handleCloseDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    // setPlatformToDelete(null);
  };

  // Updated handleViewCode to open the modal
  const handleViewCode = (sdkProject: SDKCodeResponse) => {
    console.log("View Code clicked for:", sdkProject);
    setSelectedSdkProject(sdkProject); // Store the selected project data
    setIsViewCodeModalOpen(true); // Open the modal
  };

  // Handler to close the View Code modal
  const handleCloseViewCodeModal = () => {
    setIsViewCodeModalOpen(false);
    setSelectedSdkProject(null); // Clear the selected project
  };

  // Use sdkProjects state for filtering and mapping (update later if needed)
  const filteredPlatforms = sdkProjects.filter((project) => {
    if (statusFilter === "all") return true;
    // Use the correctly imported enum for comparison
    if (statusFilter === "Installed")
      return project.status === ProjectSDKCodeStatus.INSTALLED;
    if (statusFilter !== "Installed")
      return project.status !== ProjectSDKCodeStatus.INSTALLED;
    return true;
  });

  // Format instructions for the selected SDK project (only when needed)
  const formattedInstructions = selectedSdkProject
    ? formatSDKInstructionsV2(
        selectedSdkProject,
        selectedSdkProject.codingLanguage,
      )
    : "";

  return (
    <div className="platform-content">
      <div className="header">
        <div className="dropdown-container">
          <PulseDropdown
            className="status-dropdown"
            options={[
              { label: "All Platforms", value: "all" },
              { label: "Installed", value: "Installed" },
              { label: "Not Installed", value: "Not Installed" },
            ]}
            selectedValue={statusFilter}
            onChange={(value) => setStatusFilter(value)}
            placeholder="Status"
          />
        </div>
        <PulseButton
          className="configure-button"
          startIcon={<AddRoundedIcon />}
          theme={PulseButtonTheme.SECONDARY}
          onClick={handleAddPlatform}
        >
          CONFIGURE NEW PLATFORM
        </PulseButton>
      </div>

      {isLoading && (
        <div className="loading-spinner">
          <PulseSpinnerIcon />
        </div>
      )}
      {error && <div className="error-message">{error}</div>}

      {!isLoading && !error && (
        <div className="platform-list">
          {filteredPlatforms.map((sdkProject) => (
            <PlatformCard
              key={sdkProject.sdkCodeId}
              sdkProject={sdkProject}
              onDelete={handleShowDeleteConfirm}
              onViewCode={handleViewCode}
              platform={sdkProject.platform}
              status={sdkProject.status}
              installationCode={`${sdkProject.codingLanguage} ${sdkProject.parameters?.UIEntryPoint || ""}`.toUpperCase()}
            />
          ))}
          {filteredPlatforms.length === 0 && (
            <div className="no-platforms">No platforms configured.</div>
          )}
        </div>
      )}

      {projectId && (
        <CustomDialog
          isOpen={isInstallModalOpen}
          onClose={handleCloseInstallModal}
          containerSize={PulsePopupContainerSize.CUSTOM}
          showCloseIcon={true}
          isBackgroundClose={false}
          roundedCorners={true}
        >
          <InstallSDKModal
            isOpen={isInstallModalOpen}
            onClose={handleCloseInstallModal}
            projectId={projectId}
          />
        </CustomDialog>
      )}

      <PulsePopupConfirmation
        isOpen={showDeleteConfirm}
        onClose={handleCloseDeleteConfirm}
        icon={<TrashcanIcon />}
        title="API Not Implemented"
        description="Deleting this platform is not yet available via the API. Parameters logged to console."
        confirmButtonText="OK"
        cancelButtonText="CANCEL"
        onConfirm={handleCloseDeleteConfirm}
      />

      {/* View Code Modal */}
      {selectedSdkProject && (
        <CustomDialog
          isOpen={isViewCodeModalOpen}
          onClose={handleCloseViewCodeModal}
          containerSize={PulsePopupContainerSize.CUSTOM} // Adjust size as needed
          showCloseIcon={true}
          isBackgroundClose={true} // Allow closing on background click
          roundedCorners={true}
          // title={`SDK Installation - ${selectedSdkProject.platform} (${selectedSdkProject.codingLanguage})`} // Add a title
        >
          <SDKViewer
            instructions={formattedInstructions}
            appKey={selectedSdkProject.appKeys?.[0]?.appKey}
          />
        </CustomDialog>
      )}
    </div>
  );
};

export default PlatformContent;
